import React, { useState, useRef, useEffect } from 'react';
import KPICategorySelector from './KPICategorySelector';
import organisationKPIsService, { 
  UpdateOrganisationKPIRequest,
  KPIItem,
  OrganisationKPIs 
} from '../../services/organisationKPIsService';
import { CategoryKPI } from '../../services/categoryKPIService';
import './CreateRuleModal.css';

interface KPISelection {
  categoryId: number;
  categoryName: string;
  selectedKPIs: CategoryKPI[];
}

interface EditRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRuleUpdated: () => void;
  rule: OrganisationKPIs | null;
}

const EditRuleModal: React.FC<EditRuleModalProps> = ({
  isOpen,
  onClose,
  onRuleUpdated,
  rule
}) => {
  const [selectedKPIs, setSelectedKPIs] = useState<KPISelection[]>([]);
  const [ruleText, setRuleText] = useState('');
  const [ruleWeight, setRuleWeight] = useState(50);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize form with rule data when rule changes
  useEffect(() => {
    if (rule) {
      setRuleText(rule.ruleText);
      setRuleWeight(rule.ruleWeight);
      
      // Convert existing KPIs to the selection format
      // Note: We'll need to map them to categories, but for now we'll create a generic category
      if (rule.kpis && rule.kpis.length > 0) {
        const kpiSelections: KPISelection[] = [{
          categoryId: 0, // Generic category for existing KPIs
          categoryName: 'Current KPIs',
          selectedKPIs: rule.kpis.map((kpi, index) => ({
            id: index, // Temporary ID for existing KPIs
            kpiName: kpi.name,
            kpiCode: kpi.code,
            description: kpi.description,
            categoryId: 0,
            measurementType: 'NUMERIC',
            isActive: true,
            displayOrder: index
          } as CategoryKPI))
        }];
        setSelectedKPIs(kpiSelections);
      }
    }
  }, [rule]);

  const handleKPISelectionChange = (selections: KPISelection[]) => {
    setSelectedKPIs(selections);
  };

  const handleUpdateRule = async () => {
    if (!rule) {
      alert('No rule selected for editing');
      return;
    }

    if (!ruleText.trim()) {
      alert('Please enter rule text');
      return;
    }

    if (selectedKPIs.length === 0) {
      alert('Please select at least one KPI');
      return;
    }

    try {
      setSaving(true);

      // Convert selected KPIs to the format expected by the backend
      const kpis: KPIItem[] = selectedKPIs.flatMap(selection =>
        selection.selectedKPIs.map(kpi => ({
          name: kpi.kpiName,
          description: kpi.description || '',
          code: kpi.kpiCode
        }))
      );

      const updateData: UpdateOrganisationKPIRequest = {
        kpis,
        ruleText,
        ruleWeight
      };

      await organisationKPIsService.updateOrganisationKPI(rule.id!, updateData);
      
      // Notify parent components
      onRuleUpdated();
      onClose();
      
      alert('KPI rule updated successfully!');
    } catch (err) {
      console.error('Error updating rule:', err);
      alert(err instanceof Error ? err.message : 'Failed to update KPI rule');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (rule) {
      // Reset to original values
      setRuleText(rule.ruleText);
      setRuleWeight(rule.ruleWeight);
      // Reset KPI selections would require more complex logic
    }
    onClose();
  };

  const getSelectedKPICount = () => {
    return selectedKPIs.reduce((total, selection) => total + selection.selectedKPIs.length, 0);
  };

  const handleDeselectKPI = (categoryId: number, kpiId: number) => {
    setSelectedKPIs(prevSelections => {
      return prevSelections.map(selection => {
        if (selection.categoryId === categoryId) {
          return {
            ...selection,
            selectedKPIs: selection.selectedKPIs.filter(kpi => kpi.id !== kpiId)
          };
        }
        return selection;
      }).filter(selection => selection.selectedKPIs.length > 0);
    });
  };

  const handleInsertKPI = (kpiName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const startPos = textarea.selectionStart;
    const endPos = textarea.selectionEnd;
    const currentText = ruleText;
    
    const newText = currentText.substring(0, startPos) + kpiName + currentText.substring(endPos);
    setRuleText(newText);
    
    setTimeout(() => {
      const newCursorPos = startPos + kpiName.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  if (!isOpen || !rule) return null;

  return (
    <div className="create-rule-modal-overlay">
      <div className="create-rule-modal">
        <div className="modal-header">
          <div className="modal-title">
            <span className="modal-icon">✏️</span>
            <h2>Edit KPI Rule - {rule.ruleId}</h2>
          </div>
          <button className="modal-close-button" onClick={handleCancel}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="form-section">
            <h3>Rule Details</h3>
            
            <div className="form-group">
              <label htmlFor="ruleWeight">Rule Weight (1-100) *</label>
              <input
                type="number"
                id="ruleWeight"
                value={ruleWeight}
                onChange={(e) => setRuleWeight(parseInt(e.target.value) || 1)}
                min="1"
                max="100"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="ruleText">Rule Description *</label>
              <textarea
                ref={textareaRef}
                id="ruleText"
                value={ruleText}
                onChange={(e) => setRuleText(e.target.value)}
                placeholder="Describe the validation rule and business logic..."
                rows={3}
                className="form-textarea"
              />
            </div>

            {/* Selected KPIs Display */}
            {getSelectedKPICount() > 0 && (
              <div className="selected-kpis-section">
                <h4>Selected KPIs ({getSelectedKPICount()})</h4>
                <div className="selected-kpis-cards">
                  {selectedKPIs.flatMap(selection =>
                    selection.selectedKPIs.map(kpi => (
                      <div 
                        key={`${selection.categoryId}-${kpi.id}`} 
                        className="selected-kpi-card"
                        onClick={() => handleInsertKPI(kpi.kpiName)}
                        onDoubleClick={() => handleDeselectKPI(selection.categoryId, kpi.id!)}
                        title="Click to insert into description, double-click to remove"
                      >
                        <span className="selected-kpi-name">{kpi.kpiName}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="kpi-selection-section">
            <div className="selection-header">
              <h3>Select KPIs for this Rule</h3>
              <p>Choose the KPIs you want to include in your validation rule</p>
              {getSelectedKPICount() > 0 && (
                <span className="selection-count">
                  {getSelectedKPICount()} KPIs selected
                </span>
              )}
            </div>
            
            <div className="kpi-selector-full-width">
              <KPICategorySelector 
                onSelectionChange={handleKPISelectionChange}
                initialSelections={selectedKPIs}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="save-button"
            onClick={handleUpdateRule}
            disabled={saving || !ruleText.trim() || selectedKPIs.length === 0}
          >
            {saving ? 'Updating Rule...' : 'Update Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRuleModal;
